/**
 * Maps ISO 3166-1 alpha-2 country codes to their primary ISO 639-1 language code.
 * Used to suggest languages when countries are selected for an advertiser or campaign.
 */
export const COUNTRY_TO_LANGUAGE: Record<string, string> = {
  AF: 'fa', AL: 'sq', DZ: 'ar', AD: 'ca', AO: 'pt',
  AR: 'es', AM: 'hy', AU: 'en', AT: 'de', AZ: 'az',
  BH: 'ar', BD: 'bn', BY: 'be', BE: 'nl', BZ: 'en',
  BJ: 'fr', BT: 'dz', BO: 'es', BA: 'bs', BW: 'en',
  BR: 'pt', BN: 'ms', BG: 'bg', BF: 'fr', BI: 'fr',
  KH: 'km', CM: 'fr', CA: 'en', CV: 'pt', CF: 'fr',
  TD: 'fr', CL: 'es', CN: 'zh', CO: 'es', KM: 'ar',
  CG: 'fr', CD: 'fr', CR: 'es', CI: 'fr', HR: 'hr',
  CU: 'es', CY: 'el', CZ: 'cs', DK: 'da', DJ: 'fr',
  DO: 'es', EC: 'es', EG: 'ar', SV: 'es', GQ: 'es',
  ER: 'ti', EE: 'et', ET: 'am', FI: 'fi', FR: 'fr',
  GA: 'fr', GM: 'en', GE: 'ka', DE: 'de', GH: 'en',
  GR: 'el', GT: 'es', GN: 'fr', GW: 'pt', GY: 'en',
  HT: 'fr', HN: 'es', HK: 'zh', HU: 'hu', IS: 'is',
  IN: 'hi', ID: 'id', IR: 'fa', IQ: 'ar', IE: 'en',
  IL: 'he', IT: 'it', JM: 'en', JP: 'ja', JO: 'ar',
  KZ: 'kk', KE: 'sw', KP: 'ko', KR: 'ko', KW: 'ar',
  KG: 'ky', LA: 'lo', LV: 'lv', LB: 'ar', LS: 'en',
  LR: 'en', LY: 'ar', LI: 'de', LT: 'lt', LU: 'fr',
  MO: 'zh', MK: 'mk', MG: 'mg', MW: 'en', MY: 'ms',
  MV: 'dv', ML: 'fr', MT: 'mt', MR: 'ar', MU: 'en',
  MX: 'es', MD: 'ro', MC: 'fr', MN: 'mn', ME: 'sr',
  MA: 'ar', MZ: 'pt', MM: 'my', NA: 'en', NP: 'ne',
  NL: 'nl', NZ: 'en', NI: 'es', NE: 'fr', NG: 'en',
  NO: 'no', OM: 'ar', PK: 'ur', PA: 'es', PG: 'en',
  PY: 'es', PE: 'es', PH: 'tl', PL: 'pl', PT: 'pt',
  QA: 'ar', RO: 'ro', RU: 'ru', RW: 'rw', SA: 'ar',
  SN: 'fr', RS: 'sr', SG: 'en', SK: 'sk', SI: 'sl',
  SO: 'so', ZA: 'af', ES: 'es', LK: 'si', SD: 'ar',
  SR: 'nl', SE: 'sv', CH: 'de', SY: 'ar', TW: 'zh',
  TJ: 'tg', TZ: 'sw', TH: 'th', TL: 'pt', TG: 'fr',
  TN: 'ar', TR: 'tr', TM: 'tk', UG: 'en', UA: 'uk',
  AE: 'ar', GB: 'en', US: 'en', UY: 'es', UZ: 'uz',
  VE: 'es', VN: 'vi', YE: 'ar', ZM: 'en', ZW: 'en'
};

/**
 * Maps ISO 639-1 language codes to their English display names.
 */
export const LANGUAGE_NAMES: Record<string, string> = {
  af: 'Afrikaans', am: 'Amharic', ar: 'Arabic', az: 'Azerbaijani',
  be: 'Belarusian', bg: 'Bulgarian', bn: 'Bengali', bs: 'Bosnian',
  ca: 'Catalan', cs: 'Czech', da: 'Danish', de: 'German',
  dv: 'Divehi', dz: 'Dzongkha', el: 'Greek', en: 'English',
  es: 'Spanish', et: 'Estonian', fa: 'Persian', fi: 'Finnish',
  fr: 'French', he: 'Hebrew', hi: 'Hindi', hr: 'Croatian',
  hu: 'Hungarian', hy: 'Armenian', id: 'Indonesian', is: 'Icelandic',
  it: 'Italian', ja: 'Japanese', ka: 'Georgian', kk: 'Kazakh',
  km: 'Khmer', ko: 'Korean', ky: 'Kyrgyz', lo: 'Lao',
  lt: 'Lithuanian', lv: 'Latvian', mg: 'Malagasy', mk: 'Macedonian',
  mn: 'Mongolian', ms: 'Malay', mt: 'Maltese', my: 'Burmese',
  ne: 'Nepali', nl: 'Dutch', no: 'Norwegian', pl: 'Polish',
  pt: 'Portuguese', ro: 'Romanian', ru: 'Russian', rw: 'Kinyarwanda',
  si: 'Sinhala', sk: 'Slovak', sl: 'Slovenian', so: 'Somali',
  sq: 'Albanian', sr: 'Serbian', sv: 'Swedish', sw: 'Swahili',
  tg: 'Tajik', th: 'Thai', ti: 'Tigrinya', tk: 'Turkmen',
  tl: 'Filipino', tr: 'Turkish', uk: 'Ukrainian', ur: 'Urdu',
  uz: 'Uzbek', vi: 'Vietnamese', zh: 'Chinese'
};

export interface LanguageItem {
  code: string;
  name: string;
}

/**
 * Get suggested languages for a given set of country codes (ISO 3166-1 alpha-2).
 * Returns deduplicated language list.
 */
export function getSuggestedLanguages(countryCodes: string[]): LanguageItem[] {
  if (!countryCodes || countryCodes.length === 0) return [];

  const seen = new Set<string>();
  const result: LanguageItem[] = [];

  for (const countryCode of countryCodes) {
    const langCode = COUNTRY_TO_LANGUAGE[countryCode?.toUpperCase()];
    if (langCode && !seen.has(langCode)) {
      seen.add(langCode);
      result.push({ code: langCode, name: LANGUAGE_NAMES[langCode] || langCode });
    }
  }

  return result;
}

/**
 * Get the primary language for a set of country codes (first country's language).
 */
export function getPrimaryLanguage(countryCodes: string[]): string {
  if (!countryCodes || countryCodes.length === 0) return 'en';
  const langCode = COUNTRY_TO_LANGUAGE[countryCodes[0]?.toUpperCase()];
  return langCode || 'en';
}

/**
 * Get the language name for a given language code.
 */
export function getLanguageName(languageCode: string): string {
  return LANGUAGE_NAMES[languageCode?.toLowerCase()] || languageCode;
}

/**
 * Get all available languages as a list (for dropdown/select).
 */
export function getAllLanguages(): LanguageItem[] {
  return Object.entries(LANGUAGE_NAMES).map(([code, name]) => ({ code, name }));
}
